// The takes the Tasks screen builds with, in the browser: the samples of a
// take decoded once (the same OGG the players stream, decoded by the browser
// at Opus's own 48 kHz — the numbers the server's ffmpeg decodes to, to the
// last bit but one) and its word timings as the server's montage reads them.
// Both are promises kept per take, so a take is fetched once however many
// panels ask; the decoded samples are let go oldest-first past a budget.

import { audioUrl } from './api';
import { encodeWav16 } from '../../shared/sampleOps';

export const RATE = 48000;

const SAMPLE_BUDGET = RATE * 60 * 25;   // ~25 minutes of takes in memory (≈ 290 MB of Float32)
const ALIGNMENT_RETRY_MS = 10000;        // a take without timings is asked about again after this long

const samplesByTake = new Map();   // versionId -> Promise<{samples, rate, duration}>
const samplesOrder = [];           // versionIds, oldest first
let samplesHeld = 0;
const alignmentByTake = new Map(); // versionId -> {promise, at}

async function decodeToMono(bytes) {
  const ctx = new OfflineAudioContext(1, 1, RATE);
  const buf = await new Promise((resolve, reject) => {
    // the callback form: Safari's decodeAudioData returns no promise
    const r = ctx.decodeAudioData(bytes, resolve, reject);
    if (r && typeof r.then === 'function') r.then(resolve, reject);
  });
  if (buf.numberOfChannels === 1) return new Float32Array(buf.getChannelData(0));
  const out = new Float32Array(buf.length);
  for (let c = 0; c < buf.numberOfChannels; c += 1) { const ch = buf.getChannelData(c); for (let i = 0; i < out.length; i += 1) out[i] += ch[i] / buf.numberOfChannels; }
  return out;
}

const samplesLength = new Map(); // versionId -> samples held, once decoded
function hold(versionId, length) {
  samplesOrder.push(versionId);
  samplesLength.set(versionId, length);
  samplesHeld += length;
  while (samplesHeld > SAMPLE_BUDGET && samplesOrder.length > 1) {
    const old = samplesOrder.shift();
    samplesByTake.delete(old);
    samplesHeld -= samplesLength.get(old) || 0;
    samplesLength.delete(old);
  }
}

// The decoded take: {samples: Float32Array at RATE, rate, duration}.
export function takeSamples(versionId) {
  const id = Number(versionId);
  if (samplesByTake.has(id)) return samplesByTake.get(id);
  const p = (async () => {
    const res = await fetch(audioUrl(id), { credentials: 'include' });
    if (!res.ok) throw new Error(`Take #${id}: the audio did not load (HTTP ${res.status})`);
    const samples = await decodeToMono(await res.arrayBuffer());
    hold(id, samples.length);
    return { samples, rate: RATE, duration: samples.length / RATE };
  })();
  samplesByTake.set(id, p);
  p.catch(() => { samplesByTake.delete(id); });
  return p;
}

// The take's timings {text, words, engine} or null when the aligner has not
// measured it yet (asked again after ALIGNMENT_RETRY_MS).
export function takeAlignment(versionId) {
  const id = Number(versionId);
  const have = alignmentByTake.get(id);
  if (have && (have.value !== null || Date.now() - have.at < ALIGNMENT_RETRY_MS)) return have.promise;
  const entry = { at: Date.now(), value: undefined, promise: null };
  entry.promise = (async () => {
    const res = await fetch(`/api/tasks/takes/${id}/alignment`, { credentials: 'include', cache: 'no-store' });
    const data = await res.json().catch(() => null);
    if (!res.ok) throw new Error((data && data.error) || `Take #${id}: the timings did not load (HTTP ${res.status})`);
    entry.value = data.alignment || null;
    return entry.value;
  })();
  entry.promise.catch(() => { alignmentByTake.delete(id); });
  alignmentByTake.set(id, entry);
  return entry.promise;
}

// Timings just measured elsewhere (the aligner's event): ask again next time.
export function forgetAlignment(versionId) { alignmentByTake.delete(Number(versionId)); }

// Samples as a WAV the <audio> element plays. The URL is the caller's to
// revoke (releaseUrl) once a newer one replaces it.
export function wavUrl(samples, rate = RATE) {
  const bytes = encodeWav16(samples, rate);
  return URL.createObjectURL(new Blob([bytes], { type: 'audio/wav' }));
}
export function releaseUrl(url) { if (url) { try { URL.revokeObjectURL(url); } catch (e) { /* already gone */ } } }
